import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME, homeFor } from "@/config";
import { Card, Loading } from "@/components/common";
import { GraphiteScope, Wordmark } from "@/components/Graphite";
import { Button } from "@/components/ui/button";
import { Check, EyeOff, Plug, TriangleAlert } from "lucide-react";

// The connector's Allow page (backend/connector_oauth.py, FEATURE_CONNECTOR): a founder's AI app (Claude, ChatGPT, Grok
// Bot, Claude Code, Cursor) sends them here to be allowed to read Crewbench. Read only, founders only. Allow or Deny
// sends the browser back to the app, at the address the backend checked against the app's registration.
export const ONLY_FOUNDERS = `Only founders can connect an app to ${APP_NAME}.`;
const READS = "your week, objectives, money per objective, budgets, reliability, delivery times, team, AI usage and audit log";
// an app's address to go back to: anything but a script, data or file address (the backend allows no other)
const safe = (url) => !/^\s*(javascript|data|file|vbscript|blob|about):/i.test(url || "");

function Shell({ children }) {
  return (
    <GraphiteScope className="min-h-screen grid place-items-center p-6 bg-background" data-testid="connect-authorize">
      <div className="w-full max-w-md flex flex-col gap-5">
        <Wordmark />
        <Card className="flex flex-col gap-4 px-6 py-5">{children}</Card>
      </div>
    </GraphiteScope>
  );
}

function Problem({ title, children }) {
  const { user } = useAuth();
  return (
    <Shell>
      <p className="m-0 flex items-center gap-2 font-semibold text-gr-warning"><TriangleAlert className="w-4 h-4 shrink-0" />{title}</p>
      <p className="m-0 text-muted-foreground [text-wrap:pretty]">{children}</p>
      <Button asChild variant="outline" className="self-start"><Link to={homeFor(user?.role)}>Back to {APP_NAME}</Link></Button>
    </Shell>
  );
}

export default function ConnectAuthorize() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const req = params.get("req");
  const founder = user?.role === "owner";
  const { data, error, isLoading } = useQuery({
    queryKey: ["connector-request", req], enabled: !!req && founder, retry: false, staleTime: Infinity,
    queryFn: async () => (await api.get(`/connector/requests/${encodeURIComponent(req)}`)).data,
  });
  const [going, setGoing] = useState(null);  // "allow" or "deny" while it answers, then where the browser goes
  const [failed, setFailed] = useState(null);
  useEffect(() => { document.title = `Connect an app · ${APP_NAME}`; }, []);

  if (params.get("error") === "unknown_app" || !req) {
    return <Problem title="This request to connect isn't valid">
      The app's sign-in link doesn't match an app that registered here. Start again from your AI app.</Problem>;
  }
  if (!founder) return <Problem title={ONLY_FOUNDERS}>Ask one of your company's founders to connect it.</Problem>;
  if (isLoading) return <Shell><Loading /></Shell>;
  if (error) return <Problem title="This request can't be used">{apiErr(error)}</Problem>;

  const answer = async (choice) => {
    setGoing(choice);
    setFailed(null);
    try {
      const body = choice === "allow" ? { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "" } : undefined;
      const { redirect } = (await api.post(`/connector/requests/${encodeURIComponent(req)}/${choice}`, body)).data;
      if (!safe(redirect)) throw new Error("The app's address can't be opened.");
      setGoing(redirect);
      window.location.assign(redirect);
    } catch (e) {
      setGoing(null);
      setFailed(e?.response ? apiErr(e) : e.message);
    }
  };
  const back = data.returns_to.kind === "web" ? data.returns_to.host : `an app on this computer (${data.returns_to.host})`;
  if (going && going !== "allow" && going !== "deny") {
    return <Shell>
      <p className="m-0 flex items-center gap-2 font-semibold"><Check className="w-4 h-4 text-gr-success" />Going back to {data.app}…</p>
      <p className="m-0 text-sm text-muted-foreground">If nothing opens, go back to {data.app} yourself. You can close this tab.</p>
    </Shell>;
  }
  return (
    <Shell>
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-gr-surface-2 shrink-0"><Plug className="w-4 h-4" /></span>
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">Connect an app</span>
          <h1 className="m-0 font-display text-[20px] leading-[1.2] font-semibold [overflow-wrap:anywhere]" data-testid="connect-app-name">
            Allow {data.app} to read {data.company || "your company"}'s {APP_NAME}?</h1>
        </div>
      </div>
      <div className="flex flex-col border rounded-lg overflow-hidden text-sm">
        <div className="px-3.5 py-2.5 flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">It can read</span>
          <span className="[text-wrap:pretty]">{READS[0].toUpperCase() + READS.slice(1)}.</span>
        </div>
        <div className="px-3.5 py-2.5 border-t flex gap-2 items-start text-muted-foreground">
          <EyeOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span className="[text-wrap:pretty]">It can't change anything, and it never sees your team's sessions, prompts, replies or messages.</span>
        </div>
        <div className="px-3.5 py-2.5 border-t flex flex-col gap-0.5">
          <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">Sends you back to</span>
          <span className="font-mono text-[12px] [overflow-wrap:anywhere]" data-testid="connect-returns-to">{back}</span>
        </div>
      </div>
      <p className="m-0 text-sm text-muted-foreground [text-wrap:pretty]">
        "{data.app}" is the name the app gives itself. Only allow an app you're connecting right now. You can disconnect it
        any time in Settings → Connected apps.</p>
      {failed && <p className="m-0 text-sm text-gr-warning" role="alert">{failed}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={!!going} onClick={() => answer("deny")} data-testid="connect-deny">Deny</Button>
        <Button disabled={!!going} onClick={() => answer("allow")} data-testid="connect-allow">Allow read access</Button>
      </div>
    </Shell>
  );
}
